/**
 * 逐字显示（原站 B 组件 / gdNbOU 样式组）。
 *
 * 完整还原原站节奏：
 *  - 每个字的间隔按"刚打印出来的那个字符"查表：
 *      "\n" -> 800ms，"，" -> 500ms，"。" -> 600ms，其余 120*random()+60（即 60–180ms）
 *  - 最后一个字出现后延迟 1000ms 才触发 onEnd
 *  - text 变化时，先把已显示的字按 48ms/字 反向擦除，擦完停 600ms 再重新打字
 *
 * 与原站的两处刻意差异（都是可访问性/健壮性改进，已在 README 记录）：
 *  1. 原站没有跳过；这里点击可立即显示全文
 *  2. 原站的 erase 依赖 prop 变化，这里显式管理，避免 StrictMode 双调用导致闪烁
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './typewriter.css';

/** 逐字间隔表（原站硬编码） */
const CHAR_DELAY: Record<string, number> = {
  '\n': 800,
  '，': 500,
  '。': 600,
};
/** 默认间隔区间：120 * Math.random() + 60 */
const jitter = () => 120 * Math.random() + 60;

/** 最后一个字出现后，到 onEnd 的等待 */
const END_DELAY = 1000;
/** 擦除每个字的间隔 */
const ERASE_STEP = 48;
/** 擦完到重新打字之间的停顿 */
const ERASE_PAUSE = 600;

export interface TypewriterProps {
  text: string;
  /** 全部显示完毕（含 1000ms 尾巴）的回调 */
  onEnd?: () => void;
  className?: string;
}

export function Typewriter({ text, onEnd, className }: TypewriterProps) {
  const trimmed = text.trim();
  const chars = [...trimmed];
  const [shown, setShown] = useState(0);

  const timer = useRef<number | null>(null);
  const shownRef = useRef(0);
  const cursorRef = useRef<HTMLSpanElement | null>(null);
  const contentRef = useRef<HTMLSpanElement | null>(null);
  const onEndRef = useRef(onEnd);

  onEndRef.current = onEnd;
  shownRef.current = shown;

  const clear = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  // 打字：显示到第 n 个字符后，按当前字符的间隔排下一个
  const typeFrom = useCallback(
    (index: number) => {
      clear();
      if (index >= chars.length) {
        timer.current = window.setTimeout(() => onEndRef.current?.(), END_DELAY);
        return;
      }
      const ch = chars[index];
      const delay = CHAR_DELAY[ch] ?? jitter();
      timer.current = window.setTimeout(() => {
        const next = index + 1;
        shownRef.current = next;
        setShown(next);
        typeFrom(next);
      }, delay);
    },
    [chars],
  );

  // 擦除：从当前位置逐字回退，擦完停顿再重新打字
  const eraseThenType = useCallback(() => {
    clear();
    const step = () => {
      if (shownRef.current <= 0) {
        shownRef.current = 0;
        setShown(0);
        timer.current = window.setTimeout(() => typeFrom(0), ERASE_PAUSE);
        return;
      }
      shownRef.current -= 1;
      setShown(shownRef.current);
      timer.current = window.setTimeout(step, ERASE_STEP);
    };
    step();
  }, [typeFrom]);

  // text 变化
  useEffect(() => {
    if (shownRef.current === 0) {
      typeFrom(0);
    } else {
      eraseThenType();
    }
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed]);

  // 光标跟随已显示内容的宽度
  useLayoutEffect(() => {
    const cursor = cursorRef.current;
    const content = contentRef.current;
    if (!cursor || !content) return;
    const spans = content.querySelectorAll('span');
    if (!spans.length || shown === 0) {
      cursor.style.transform = 'translate(0px, 0px)';
      return;
    }
    const last = spans[Math.min(shown, spans.length) - 1] as HTMLElement;
    const contentBox = content.getBoundingClientRect();
    const lastBox = last.getBoundingClientRect();
    const x = lastBox.left - contentBox.left + lastBox.width;
    const y = lastBox.top - contentBox.top + (lastBox.height - cursor.offsetHeight) / 2;
    cursor.style.transform = `translate(${x}px, ${y}px)`;
  }, [shown]);

  const skip = useCallback(() => {
    clear();
    shownRef.current = chars.length;
    setShown(chars.length);
    timer.current = window.setTimeout(() => onEndRef.current?.(), END_DELAY);
  }, [chars.length]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={['typeText', className].filter(Boolean).join(' ')} onClick={skip}>
      <span className="contentWrapper">
        <span className="content" ref={contentRef}>
          {chars.map((ch, i) => (
            <span key={i} style={{ opacity: i < shown ? 1 : 0 }}>
              {ch}
            </span>
          ))}
        </span>
        <span className="cursor" ref={cursorRef} aria-hidden="true" />
      </span>
    </div>
  );
}
